import { api } from "./client";
import type { ScrewThresholds } from "../utils/screwSpeed";

/**
 * Blender-PLC extruder endpoints, backed by `kgreading`.
 *
 * `getExtruderSettings` is live and plant-wide: settings_extruders is a single
 * row, read by the overview diagram and the Lamination 1 extruder dashboard.
 *
 * The three series calls below are HISTORY ONLY. Lamination 1's extruder
 * signals were wired to the blender-1 PLC and written to kgreading until Line
 * 1's PLC was replaced in Sept 2026; since 2026-08-27 those columns have read
 * as noise (max raw value 1, against ~14900 before). Live extruder data now
 * comes from the line's own PLC via `api/lam1.ts`. Rows written before the swap
 * are still real, which is why these remain — nothing calls them today.
 */

/** Server-side time-bucket granularity. Omit for raw rows (full resolution). */
export type ExtruderGroupBy = "minute" | "hour" | "day" | "week" | "month";

/** The plant's extruder bands. Same type the classifier uses — see
 *  utils/screwSpeed, which owns the rule these values feed. */
export type ExtruderSettings = ScrewThresholds;

export function getExtruderSettings(signal?: AbortSignal) {
  return api
    .get<Partial<ExtruderSettings>>("/settings/extruders", { signal })
    .then((r) => r.data);
}

/** Events/status for one extruder
 *  GET /blender/extruders?start=ISO&end=ISO&extruder=1|2
 *  Returns rows like: { timestamp, extruder1?: boolean|number, extruder2?: boolean|number, ... }
 */
export function getExtruderEvents(params: {
  start: string;
  end: string;
  extruder: "1" | "2";
  group_by?: ExtruderGroupBy;
  signal?: AbortSignal;
}) {
  const { signal, ...query } = params;
  return api
    .get<any[]>("/blender/extruders", { params: query, signal })
    .then((r) => r.data);
}

/** Motor currents for one extruder
 *  GET /blender/extruders/motor_currents?start=ISO&end=ISO&extruder=1|2
 *  Returns rows with timestamp/period and fields like:
 *   - extruder{N}_current_a, extruder{N}_motor_current,
 *   - extruder{N}_current_a_avg, extruder{N}_motor_current_avg
 */
export function getExtruderMotorCurrents(params: {
  start: string;
  end: string;
  extruder: "1" | "2";
  group_by?: ExtruderGroupBy;
  /** Adaptive downsampling target (buckets). Omit group_by to use. */
  max_points?: number;
  signal?: AbortSignal;
}) {
  const { signal, ...query } = params;
  return api
    .get<any[]>("/blender/extruders/motor_currents", { params: query, signal })
    .then((r) => r.data);
}

/** Screw speeds for one extruder
 *  GET /blender/extruders/screw_speeds?start=ISO&end=ISO&extruder=1|2
 *  Returns rows with timestamp/period and fields like:
 *   - extruder{N}_speed_hz, extruder{N}_screw_speed_hz,
 *   - extruder{N}_speed_hz_avg, extruder{N}_screw_speed_hz_avg
 */
export function getExtruderScrewSpeeds(params: {
  start: string;
  end: string;
  extruder: "1" | "2";
  group_by?: ExtruderGroupBy;
  /** Adaptive downsampling target (buckets). Omit group_by to use. */
  max_points?: number;
  signal?: AbortSignal;
}) {
  const { signal, ...query } = params;
  return api
    .get<any[]>("/blender/extruders/screw_speeds", { params: query, signal })
    .then((r) => r.data);
}
